/**
 * The intent emitter (P2 design §4.6): `intent.<name>` when a user operation starts,
 * `intent.<name>.end` with its outcome, and the point intents (`forecast.result_seen`).
 * `instrument.ts`'s `beginIntent` / `withIntent` are the API; this module is their body.
 *
 * - Off (no-op handles with an empty id) until telemetry.js applies an /api/me answer with
 *   `telemetryIntentEnabled`; params are filtered to the intent's registered keys and types,
 *   so nothing unregistered (and never free text) is recorded.
 * - `superseded`: a new UI-local intent (scope `ui`) or chat turn ends the open one of the
 *   same name. Long-running intents are never superseded.
 * - Long-running intents (`forecast.submit`, `branch`, `reforecast`, `edit_resubmit`) end with
 *   their RUN, wherever the user is meanwhile. The command attaches the run's client id
 *   (`attachRun`), and the forecast stream binds the run's trace id (`bindTrace`). Bound runs
 *   live in localStorage, scoped to the user and org, for `RUN_TTL_MS`: any tab that sees the
 *   run end — its stream, a resume after a reload, or the run-store after a realtime hint's
 *   read-through (services/intentRuns.js) — ends the intent. The end is claimed under a Web
 *   Lock named for the intent and marked in the shared map, so it is recorded once.
 * - `forecast.result_seen`: the first time a finished run's result surface (timeline, report,
 *   brief) is attended (services/attentionTracker.js), once per user and intent, with `via` and
 *   `delay_ms` since the run ended.
 */
import { INTENTS } from "./registry";
import { recordIntentEvent } from "./groups.js";
import { recentSurface } from "./recentControl.js";

export const RUNS_KEY_PREFIX = "sooth.telemetry.intentRuns.v1:";
/** Bound runs are kept 8 days: the 7-day `result_seen` window plus overlap. */
export const RUN_TTL_MS = 8 * 24 * 60 * 60 * 1000;
/** Most bound runs kept per user; the oldest go first. */
export const RUNS_MAX = 200;
export const END_LOCK_PREFIX = "sooth-intent-end:";
/** Surfaces that show a run's result: attending one of them counts as seeing it. */
export const RESULT_SURFACES = new Set([
  "forecast-header",
  "timeline",
  "timeline.axis",
  "timeline.canvas",
  "report",
  "report.section",
  "brief",
  "evidence.card",
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Longest id-shaped string param kept. */
const MAX_PARAM = 128;

export const isIntentId = (v) => typeof v === "string" && UUID_RE.test(v);

/** The `x-intent-id` header for a request an intent issues; `{}` when there is none. */
export function intentHeaders(intentId) {
  return isIntentId(intentId) ? { "x-intent-id": intentId } : {};
}

const NOOP_HANDLE = Object.freeze({
  id: "",
  end() {},
  mark() {},
  attachRun() {},
  bindTrace() {},
});

/** `value` as registry type `type`, or undefined when it does not fit. */
function cleanParam(type, value) {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(type)) return type.includes(value) ? value : undefined;
  switch (type) {
    case "str":
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
      return typeof value === "string" && value.length > 0 ? value.slice(0, MAX_PARAM) : undefined;
    case "int":
      return typeof value === "number" && Number.isFinite(value) ? Math.round(value) : undefined;
    case "num":
      return typeof value === "number" && Number.isFinite(value) ? value : undefined;
    case "bool":
      return typeof value === "boolean" ? value : undefined;
    case "strs":
      return Array.isArray(value)
        ? value.filter((v) => typeof v === "string").map((v) => v.slice(0, MAX_PARAM))
        : undefined;
    default:
      return undefined;
  }
}

/** The registered params of `entry` present in `params`, each of its registered type. */
export function cleanParams(entry, params) {
  const out = {};
  if (!params || typeof params !== "object") return out;
  for (const [k, type] of Object.entries(entry.params)) {
    const v = cleanParam(type, params[k]);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

const supersedes = (name) => INTENTS[name]?.scope === "ui" || name === "chat.ask";

/** `via` of a `forecast.result_seen`: how the user came back for the result. */
export function viaOf({ entrySource, startedTab, startedVisit, tabId, visitId }) {
  if (entrySource === "alert") return "alert_email";
  if (entrySource === "link" || entrySource === "copy_link") return "link";
  if (startedTab && startedTab === tabId && startedVisit && startedVisit === visitId) {
    return "same_tab";
  }
  return "later_visit";
}

function safeLocalStorage() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function defaultNewId() {
  try {
    return crypto.randomUUID();
  } catch {
    return null;
  }
}

/**
 * Create an emitter; the app uses the singleton below. Every side effect is injectable:
 * `record` (the intent group's recorder), `storage` (localStorage), `locks` (Web Locks),
 * `wallNow` (ms since the epoch: bound runs cross tabs and reloads), `surfaceOf` (the surface
 * of the control that started an intent, from its `origin`; lib/telemetry/recentControl.js),
 * and the getters the `result_seen` `via` reads.
 */
export function createIntentEmitter({
  record = recordIntentEvent,
  storage = safeLocalStorage(),
  locks = typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null,
  wallNow = () => Date.now(),
  newId = defaultNewId,
  surfaceOf = recentSurface,
  getTabId = () => null,
  getVisitId = () => null,
  getEntrySource = () => "direct",
} = {}) {
  let enabled = false;
  /**
   * False until the first `configure` (the first /api/me answer). Until then intents are
   * opened provisionally: their events are held by the group until an answer decides
   * (lib/telemetry/groups.js), and nothing is written to the shared run map (no identity yet).
   */
  let answered = false;
  /**
   * Bumped when an answer changes the identity: handles opened under an earlier identity go
   * inert, so a late `end` never lands under the new account. Handles opened before the first
   * answer belong to generation 0 and stay live through it.
   */
  let generation = 0;
  /** `user|org` the bound runs are stored under; null while off. */
  let scope = null;
  /** The open UI-local intent / chat turn per name, for `superseded`. */
  const open = new Map();
  /** Long-running handles by the client run id they attached, until they end. */
  const byRun = new Map();
  /** Long-running handles by trace id, while this tab holds them. */
  const byTrace = new Map();
  /** The parsed bound-run map, or null when it must be re-read. */
  let cache = null;
  /** Storage keys of records past the TTL that the last read skipped (pruned on the next write). */
  const expiredKeys = new Set();
  /** Intent ids this tab ended or saw `result_seen` for (fast path before the shared map). */
  const endedHere = new Set();
  const seenHere = new Set();

  /**
   * Each bound run is its own localStorage key under the user's scope, so tabs that bind or
   * end different runs never overwrite each other's records.
   */
  const keyPrefix = () => (scope ? `${RUNS_KEY_PREFIX}${scope}:` : null);

  function readRuns() {
    if (cache) return cache;
    const prefix = keyPrefix();
    const runs = {};
    const cutoff = wallNow() - RUN_TTL_MS;
    try {
      const n = prefix && storage ? storage.length : 0;
      for (let i = 0; i < n; i++) {
        const k = storage.key(i);
        if (!k || !k.startsWith(prefix)) continue;
        try {
          const v = JSON.parse(storage.getItem(k));
          if (!v || typeof v !== "object" || Array.isArray(v)) continue;
          // Past the TTL a record is gone, whether or not a write has pruned it yet.
          if (typeof v.t0 !== "number" || v.t0 < cutoff) {
            expiredKeys.add(k);
            continue;
          }
          runs[k.slice(prefix.length)] = v;
        } catch {
          /* a corrupt record is skipped */
        }
      }
    } catch {
      /* storage unavailable */
    }
    cache = runs;
    return runs;
  }

  /**
   * Apply `fn` to a fresh copy of the bound runs, prune them, and write back only the records
   * `fn` replaced (and remove the pruned ones).
   */
  function updateRuns(fn) {
    const prefix = keyPrefix();
    if (!prefix) return;
    cache = null;
    const before = readRuns();
    const runs = { ...before };
    fn(runs);
    const cutoff = wallNow() - RUN_TTL_MS;
    const kept = Object.entries(runs)
      .filter(([, r]) => r && typeof r === "object" && typeof r.t0 === "number" && r.t0 >= cutoff)
      .sort((a, b) => b[1].t0 - a[1].t0)
      .slice(0, RUNS_MAX);
    const keep = new Set(kept.map(([k]) => k));
    try {
      for (const [k, r] of kept)
        if (r !== before[k]) storage?.setItem(prefix + k, JSON.stringify(r));
      for (const k of Object.keys(before)) if (!keep.has(k)) storage?.removeItem(prefix + k);
      for (const k of expiredKeys) storage?.removeItem(k);
      expiredKeys.clear();
    } catch {
      /* quota / private mode: this tab still ends its own runs */
    }
    cache = Object.fromEntries(kept);
  }

  /** Run `fn` holding the Web Lock for `intentId`; without Web Locks, now. */
  function underLock(intentId, fn) {
    const run = () => {
      try {
        fn();
      } catch {
        /* never surface */
      }
    };
    if (!locks || typeof locks.request !== "function") {
      run();
      return;
    }
    try {
      Promise.resolve(locks.request(END_LOCK_PREFIX + intentId, run)).catch(() => run());
    } catch {
      run();
    }
  }

  function emitEnd(rec, outcome, milestones) {
    const entry = INTENTS[rec.name];
    if (!entry) return;
    const duration = Math.max(0, Math.round(wallNow() - rec.t0));
    const marks = {};
    for (const m of entry.milestones) {
      const v = milestones?.[m] ?? rec.marks?.[m];
      if (typeof v === "number" && Number.isFinite(v)) marks[m] = Math.max(0, Math.round(v));
    }
    if (outcome === "ok" && entry.milestones.includes("result_ms") && marks.result_ms == null) {
      marks.result_ms = duration;
    }
    record({
      event: `intent.${rec.name}.end`,
      intent_id: rec.intent_id,
      outcome,
      duration_ms: duration,
      ...marks,
    });
  }

  /**
   * End a long-running intent bound to runs in the shared map: under its lock, unless some
   * tab already marked it ended.
   */
  function claimEnd(rec, outcome, milestones) {
    if (endedHere.has(rec.intent_id)) return;
    underLock(rec.intent_id, () => {
      if (endedHere.has(rec.intent_id)) return;
      cache = null;
      const runs = readRuns();
      const already = Object.values(runs).some(
        (r) => r?.intent_id === rec.intent_id && typeof r.ended_at === "number",
      );
      endedHere.add(rec.intent_id);
      if (already) return;
      const at = wallNow();
      updateRuns((m) => {
        for (const [k, r] of Object.entries(m)) {
          if (r?.intent_id === rec.intent_id) m[k] = { ...r, ended_at: at, outcome };
        }
      });
      emitEnd(rec, outcome, milestones);
    });
  }

  function makeHandle(name, entry, id, t0) {
    const h = {
      id,
      gen: generation,
      /**
       * Whether commands bind their requests and actions to it (lib/telemetry/intentDeps.ts):
       * not for a provisional intent opened before any answer.
       */
      correlate: answered && enabled,
      name,
      t0,
      marks: {},
      ended: false,
      runIds: new Set(),
      traces: new Set(),
      end(outcome, milestones) {
        if (h.ended || h.gen !== generation) return;
        h.ended = true;
        if (open.get(name) === h) open.delete(name);
        for (const r of h.runIds) if (byRun.get(r) === h) byRun.delete(r);
        for (const t of h.traces) if (byTrace.get(t) === h) byTrace.delete(t);
        if (outcome === "abandoned" && entry.scope !== "ui") outcome = "cancelled";
        const rec = { intent_id: id, name, t0, marks: h.marks };
        if (h.traces.size > 0) claimEnd(rec, outcome, milestones);
        else {
          endedHere.add(id);
          // A run with no trace (a derived branch, the tour) is recorded once it ends, so its
          // result can still be seen (`noteAttended`).
          if (entry.scope === "long-running" && h.runIds.size > 0) {
            const at = wallNow();
            updateRuns((m) => {
              for (const runId of h.runIds) {
                m[`run:${runId}`] = {
                  intent_id: id,
                  name,
                  t0,
                  marks: { ...h.marks },
                  run_id: runId,
                  primary: true,
                  tab: getTabId(),
                  visit: getVisitId(),
                  ended_at: at,
                  outcome,
                };
              }
            });
          }
          emitEnd(rec, outcome, milestones);
        }
      },
      mark(milestone) {
        if (h.ended || h.gen !== generation) return;
        if (!entry.milestones.includes(milestone) || milestone in h.marks) return;
        h.marks[milestone] = Math.max(0, Math.round(wallNow() - t0));
        if (h.traces.size > 0) {
          updateRuns((m) => {
            for (const t of h.traces) if (m[t]) m[t] = { ...m[t], marks: { ...h.marks } };
          });
        }
      },
      /** The client run id this long-running intent follows (services/intentRuns.js). */
      attachRun(runId) {
        if (h.ended || h.gen !== generation || typeof runId !== "string" || !runId) return;
        h.runIds.add(runId);
        byRun.set(runId, h);
      },
      /**
       * A stream of this intent got its trace id. The run the intent follows (an attached
       * run, or any run while none is attached) is PRIMARY: from here any tab can end the
       * intent with it. Other runs the intent fans out (a reforecast's branch reruns, a
       * compare leg) only carry its id.
       */
      bindTrace(traceId, { runId = null } = {}) {
        if (h.ended || h.gen !== generation || typeof traceId !== "string" || !traceId) return;
        const primary = !runId || h.runIds.size === 0 || h.runIds.has(runId);
        if (primary) {
          h.traces.add(traceId);
          byTrace.set(traceId, h);
          if (runId) h.attachRun(runId);
        }
        const run = runId ?? [...h.runIds][0] ?? null;
        updateRuns((m) => {
          m[traceId] = {
            intent_id: id,
            name,
            t0,
            marks: { ...h.marks },
            run_id: run,
            primary,
            tab: getTabId(),
            visit: getVisitId(),
          };
        });
      },
    };
    return h;
  }

  function begin(name, params, opts = {}) {
    const entry = INTENTS[name];
    if ((answered && !enabled) || !entry) return NOOP_HANDLE;
    try {
      const id = isIntentId(opts?.id) ? opts.id.toLowerCase() : newId();
      if (!id) return NOOP_HANDLE;
      const clean = cleanParams(entry, params);
      if (entry.scope === "point") {
        record({ event: name, intent_id: id, ...clean });
        return { id };
      }
      if (supersedes(name)) open.get(name)?.end("superseded");
      const t0 = wallNow();
      record({
        event: `intent.${name}`,
        intent_id: id,
        origin: typeof opts?.origin === "string" ? opts.origin : null,
        surface:
          typeof opts?.surface === "string" ? opts.surface : (surfaceOf(opts?.origin) ?? null),
        v: entry.v,
        ...clean,
      });
      const h = makeHandle(name, entry, id, t0);
      if (supersedes(name)) open.set(name, h);
      return h;
    } catch {
      return NOOP_HANDLE;
    }
  }

  /** This emitter's `begin`, under the name the instrumentation scanner reads. */
  const beginIntent = begin;

  /** The bound run of `traceId` in the shared map, or null. */
  function runOf(traceId) {
    if (!enabled || typeof traceId !== "string") return null;
    const r = readRuns()[traceId];
    return r && typeof r === "object" && isIntentId(r.intent_id) ? r : null;
  }

  return {
    /**
     * Apply an /api/me answer: on or off, and the user and org bound runs are kept under. A
     * new identity forgets this tab's open intents (they belonged to the previous account).
     * `context` replaces the getters `result_seen` reads (`tabId`, `visitId`, `entrySource`).
     */
    configure({ on = false, user = null, org = null, context = null } = {}) {
      if (context) {
        if (typeof context.tabId === "function") getTabId = context.tabId;
        if (typeof context.visitId === "function") getVisitId = context.visitId;
        if (typeof context.entrySource === "function") getEntrySource = context.entrySource;
      }
      const nextScope = on ? `${user ?? "anon"}|${org ?? ""}` : null;
      // The first answer keeps what was opened before it (generation 0); a later identity
      // change starts over.
      if (answered && nextScope !== scope) {
        generation += 1;
        open.clear();
        byRun.clear();
        byTrace.clear();
        endedHere.clear();
        seenHere.clear();
        cache = null;
      }
      scope = nextScope;
      enabled = on === true;
      answered = true;
    },
    get enabled() {
      return enabled;
    },
    begin,
    /** The intent id bound to `traceId` (a resume stream inherits it), or null. */
    intentIdForTrace(traceId) {
      const live = byTrace.get(traceId);
      if (live) return live.id;
      return runOf(traceId)?.intent_id ?? null;
    },
    /** The intent id following client run `runId` in this tab, or null. */
    intentIdForRun(runId) {
      return byRun.get(runId)?.id ?? null;
    },
    /**
     * The run `fromRunId`'s intent now follows `toRunId` instead (a rerun that replaces the
     * pending run, e.g. a resolution moment picked while the forecast ran).
     */
    retargetRun(fromRunId, toRunId) {
      const h = byRun.get(fromRunId);
      if (!h || typeof toRunId !== "string" || !toRunId) return;
      byRun.delete(fromRunId);
      h.runIds.delete(fromRunId);
      // The replaced run's traces no longer end the intent, here or in any other tab.
      const stale = Object.entries(readRuns())
        .filter(([, r]) => r?.intent_id === h.id && r.run_id === fromRunId)
        .map(([t]) => t);
      for (const t of stale) {
        h.traces.delete(t);
        if (byTrace.get(t) === h) byTrace.delete(t);
      }
      if (stale.length > 0) {
        updateRuns((m) => {
          for (const t of stale) if (m[t]) m[t] = { ...m[t], primary: false };
        });
      }
      h.attachRun(toRunId);
    },
    /** Bind `traceId` to the intent `intentId` this tab holds (the forecast stream opened). */
    bindTrace(intentId, traceId, opts) {
      if (!enabled || !isIntentId(intentId)) return;
      for (const h of new Set([...byRun.values(), ...byTrace.values()])) {
        if (h.id === intentId) {
          h.bindTrace(traceId, opts);
          return;
        }
      }
    },
    /** A milestone of the run on `traceId` (`first_frame_ms`), in whichever tab sees it. */
    markRun(traceId, milestone) {
      if (!enabled) return;
      const live = byTrace.get(traceId);
      if (live) {
        live.mark(milestone);
        return;
      }
      const r = runOf(traceId);
      if (!r || r.primary === false || typeof r.ended_at === "number") return;
      if (r.marks?.[milestone] != null) return;
      if (!INTENTS[r.name]?.milestones.includes(milestone)) return;
      updateRuns((m) => {
        const cur = m[traceId];
        if (cur && cur.marks?.[milestone] == null) {
          m[traceId] = {
            ...cur,
            marks: { ...cur.marks, [milestone]: Math.max(0, Math.round(wallNow() - cur.t0)) },
          };
        }
      });
    },
    /**
     * The run on `traceId` (or client run `runId`) ended: end its long-running intent, once
     * across tabs. `outcome` is ok / error / cancelled.
     */
    endRun({ traceId = null, runId = null } = {}, outcome) {
      if (!enabled) return;
      const live = (traceId && byTrace.get(traceId)) || (runId && byRun.get(runId)) || null;
      if (live) {
        live.end(outcome);
        return;
      }
      const r = traceId ? runOf(traceId) : null;
      if (!r || r.primary === false || typeof r.ended_at === "number") return;
      claimEnd({ intent_id: r.intent_id, name: r.name, t0: r.t0, marks: r.marks || {} }, outcome);
    },
    /**
     * A surface was attended: when it shows a finished run's result that this user has not
     * seen yet, record `forecast.result_seen`.
     */
    noteAttended(surfaceName, forecastId) {
      if (!enabled || !RESULT_SURFACES.has(surfaceName) || !forecastId) return;
      const runs = readRuns();
      for (const r of Object.values(runs)) {
        if (
          r?.run_id !== forecastId ||
          r.primary === false ||
          r.outcome !== "ok" ||
          typeof r.ended_at !== "number" ||
          r.seen_at != null ||
          seenHere.has(r.intent_id)
        ) {
          continue;
        }
        const rec = r;
        seenHere.add(rec.intent_id);
        underLock(rec.intent_id, () => {
          cache = null;
          const cur = Object.values(readRuns()).find((x) => x?.intent_id === rec.intent_id);
          if (!cur || cur.seen_at != null) return;
          const at = wallNow();
          updateRuns((m) => {
            for (const [k, x] of Object.entries(m)) {
              if (x?.intent_id === rec.intent_id) m[k] = { ...x, seen_at: at };
            }
          });
          // A point intent under the run's own intent id (no `.end`).
          beginIntent(
            "forecast.result_seen",
            {
              forecast_id: forecastId,
              via: viaOf({
                entrySource: getEntrySource(),
                startedTab: rec.tab,
                startedVisit: rec.visit,
                tabId: getTabId(),
                visitId: getVisitId(),
              }),
              delay_ms: Math.max(0, Math.round(at - rec.ended_at)),
            },
            { id: rec.intent_id },
          );
        });
      }
    },
    /** Another tab wrote the bound-run map: re-read it on the next use. */
    invalidate(key) {
      if (key == null || (typeof key === "string" && key.startsWith(RUNS_KEY_PREFIX))) cache = null;
    },
  };
}

let singleton = null;

/** The app's emitter. */
export function intentEmitter() {
  if (!singleton) singleton = createIntentEmitter();
  return singleton;
}

/**
 * CLEAR discarded `run`: a run with no trace yet (preprocessing, or held for a resolution
 * choice) has no server run to finish, so the intent following it ends `cancelled`. A run
 * with a trace keeps streaming after CLEAR and ends its intent itself.
 */
export function endDiscardedRunIntent(run) {
  if (!run?.id || run.traceId) return;
  intentEmitter().endRun({ runId: run.id }, "cancelled");
}

/** Replace the singleton (tests); returns the previous one. */
export function setIntentEmitter(next) {
  const prev = singleton;
  singleton = next;
  return prev;
}
